import { create } from 'zustand';
import type { AppCurrency, AuthResponse, AuthTokens, UserRole } from '@financial/shared';
import { configureAuth } from '../lib/api';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  defaultCurrency: AppCurrency;
  aiConsentGranted: boolean;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  hydrated: boolean;
  isAuthenticated: () => boolean;
  setSession: (res: AuthResponse) => void;
  setTokens: (tokens: AuthTokens) => void;
  setUser: (user: AuthUser) => void;
  clear: () => void;
}

const STORAGE_KEY = 'financial.auth';

interface Persisted {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
}

function load(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Persisted;
  } catch {
    /* localStorage no disponible o corrupto: arrancamos sin sesión */
  }
  return { accessToken: null, refreshToken: null, user: null };
}

function save(state: Persisted): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function persistFrom(get: () => AuthState): void {
  const { accessToken, refreshToken, user } = get();
  save({ accessToken, refreshToken, user });
}

const initial = load();

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: initial.accessToken,
  refreshToken: initial.refreshToken,
  user: initial.user,
  hydrated: true,
  isAuthenticated: () => Boolean(get().accessToken),
  setSession: (res) => {
    set({ accessToken: res.accessToken, refreshToken: res.refreshToken, user: res.user });
    persistFrom(get);
  },
  setTokens: (tokens) => {
    set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
    persistFrom(get);
  },
  setUser: (user) => {
    set({ user });
    persistFrom(get);
  },
  clear: () => {
    set({ accessToken: null, refreshToken: null, user: null });
    save({ accessToken: null, refreshToken: null, user: null });
  },
}));

// Conecta el store con el cliente HTTP (token getters + manejo de refresh/logout).
configureAuth({
  getAccessToken: () => useAuthStore.getState().accessToken,
  getRefreshToken: () => useAuthStore.getState().refreshToken,
  onTokens: (tokens) => useAuthStore.getState().setTokens(tokens),
  onAuthFailure: () => useAuthStore.getState().clear(),
});
